import React, { useEffect, useState, useCallback } from 'react';
import { Box, Heading, Container, Text, Button, Flex, Image, Stack, useToast, Alert, AlertIcon, Input, IconButton, HStack } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { setCart } from '../features/cart/cartSlice';
import Loader from '../components/Loader';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FaPlus, FaMinus } from 'react-icons/fa';
import { useDebouncedCallback } from 'use-debounce';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionButton = motion(Button);

const CartPage = () => {
  const cart = useSelector(state => state.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRemoving, setIsRemoving] = useState(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/cart/');
      dispatch(setCart(response.data));
      setValidationErrors({}); // Clear previous errors on fetch
    } catch (err) {
      setError('Failed to fetch your cart. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      const response = await axios.patch(`/api/cart/item/${itemId}/update/`, { quantity: newQuantity });
      dispatch(setCart(response.data));
      // Clear error for this specific item
      setValidationErrors(prev => ({ ...prev, [itemId]: null }));
    } catch (error) {
      toast({
        title: 'Error updating quantity',
        description: error.response?.data?.error || 'Could not update the quantity.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const debouncedUpdateQuantity = useDebouncedCallback(handleUpdateQuantity, 500);

  const handleRemoveItem = async (itemId) => {
    setIsRemoving(itemId);
    try {
      await axios.delete(`/api/cart/item/${itemId}/delete/`);
      toast({
        title: 'Item removed',
        description: `Item has been removed from your cart`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchCart();
    } catch (error) {
      toast({
        title: 'Error removing item',
        description: 'Could not remove the item from your cart. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsRemoving(null);
    }
  };

  const handleValidateCart = async () => {
    setIsCheckingOut(true);
    setValidationErrors({});
    try {
      const itemsToValidate = cart.items.map(item => ({ id: item.id, quantity: item.quantity }));
      await axios.post('/api/cart/validate/', itemsToValidate);
      navigate('/checkout', { state: { from: 'cart' } });
    } catch (error) {
      if (error.response && error.response.data.errors) {
        const newErrors = {};
        error.response.data.errors.forEach(err => {
          const item = cart.items.find(i => i.id === err.id);
          const productName = item ? item.product.name : `Item ID ${err.id}`;
          
          newErrors[err.id] = err.error;
          toast({
            title: 'Validation Error',
            description: `For ${productName}: ${err.error}`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });

          // Auto-correct quantity or remove item
          const stockMatch = err.error.match(/Only (\d+) items available/);
          if (stockMatch) {
            const availableStock = parseInt(stockMatch[1], 10);
            if (availableStock > 0) {
              handleUpdateQuantity(err.id, availableStock);
            } else {
              handleRemoveItem(err.id);
            }
          } else if (err.error.includes('out of stock')) {
            handleRemoveItem(err.id);
          }
        });
        setValidationErrors(newErrors);
      } else {
        toast({
          title: 'An unexpected error occurred',
          description: 'Please try again later.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={12}>
        <Loader minH="300px" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={12}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <MotionBox 
      py={{ base: 8, md: 12 }}
      minH="calc(100vh - 200px)"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      position="relative"
      bgImage={`url(${process.env.REACT_APP_BG_URL})`}
    >
      <Container maxW="container.xl" pb={{ base: "140px", md: "100px" }}>
        <Heading 
          as="h1" 
          size="xl" 
          mb={8} 
          textAlign="center"
          fontFamily="'Cormorant', serif"
          fontWeight="700"
          fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
          letterSpacing="wide"
          color="gray.800"
        >
          Shopping Cart
        </Heading>
        {
          !cart || cart.items.length === 0 ? (
            <MotionFlex 
              direction="column" 
              align="center" 
              justify="center" 
              minH="300px"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Text 
                fontSize={{ base: "lg", md: "xl" }} 
                mb={4}
                fontFamily="'Zain', sans-serif"
                fontWeight="500"
                color="gray.600"
              >
                Your cart is empty.
              </Text>
              <Button 
                as={RouterLink} 
                to="/products/rings" 
                colorScheme="blackAlpha"
                bg="black"
                color="white"
                fontFamily="'Zain', sans-serif"
                fontWeight="600"
                _hover={{ bg: "gray.800", transform: "scale(1.05)" }}
              >
                Start Shopping
              </Button>
            </MotionFlex>
          ) : (
            <Stack spacing={6}>
              {cart.items.map((item, index) => (
                <MotionFlex 
                  key={item.id} 
                  p={{ base: 4, md: 4 }} 
                  bg="rgb(115, 147, 179, 0.2)"
                  borderWidth="1px" 
                  borderRadius="lg" 
                  align={{ base: 'flex-start', md: 'center' }}
                  justify="space-between"
                  direction={{ base: 'column', md: 'row' }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                  isInvalid={!!validationErrors[item.id]}
                >
                  {/* Mobile: Row layout with image and content side by side */}
                  <Flex 
                    align={{ base: 'flex-start', md: 'center' }} 
                    direction={{ base: 'row', md: 'row' }} 
                    w="full"
                    mb={{ base: 3, md: 0 }}
                  >
                    <Image 
                      src={item.product.primaryImage || '/no_image_available.jpg'} 
                      alt={item.product.name} 
                      boxSize={{ base: "120px", md: "100px" }} 
                      objectFit="cover" 
                      mr={4}
                      borderRadius="md"
                      flexShrink={0}
                    />
                    <Box flex="1" textAlign="left">
                      <Text 
                        fontWeight="900" 
                        fontSize={{ base: "xl", md: "2xl" }}
                        fontFamily="'Cormorant', serif"
                        color="gray.800"
                        mb={1}
                      >
                        {item.product.name}
                      </Text>
                      <Text 
                        color="gray.700" 
                        fontSize={{ base: "md", md: "xl" }}
                        fontFamily="'Zain', sans-serif"
                        fontWeight="600"
                        mb={1}
                      >
                        Rs {item.subtotal}
                      </Text>
                      {item.size && (
                        <Text 
                          color="gray.500" 
                          fontSize="sm"
                          fontFamily="'Zain', sans-serif"
                          fontWeight="500"
                        >
                          Size: {item.size}
                        </Text>
                      )}
                      {validationErrors[item.id] && (
                        <Text 
                          color="red.500" 
                          fontSize="sm" 
                          mt={1}
                          fontFamily="'Zain', sans-serif"
                          fontWeight="500"
                        >
                          {validationErrors[item.id]}
                        </Text>
                      )}
                    </Box>
                  </Flex>

                  {/* Quantity and Remove button - side by side on mobile, separate on desktop */}
                  <Flex 
                    direction={{ base: 'row', md: 'row' }}
                    align="center"
                    justify={{ base: 'space-between', md: 'flex-end' }}
                    w={{ base: 'full', md: 'auto' }}
                    gap={{ base: 3, md: 0 }}
                  >
                    <HStack spacing={2}>
                      <IconButton 
                        icon={<FaMinus />} 
                        size="sm" 
                        onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.300"
                        _hover={{ bg: "gray.100" }}
                      />
                      <Input 
                        type="number" 
                        value={item.quantity} 
                        onChange={(e) => debouncedUpdateQuantity(item.id, parseInt(e.target.value, 10))} 
                        textAlign="center" 
                        w="60px"
                        fontFamily="'Zain', sans-serif"
                        fontWeight="600"
                      />
                      <IconButton 
                        icon={<FaPlus />} 
                        size="sm" 
                        onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.300"
                        _hover={{ bg: "gray.100" }}
                      />
                    </HStack>
                    
                    <MotionButton 
                      colorScheme="red" 
                      onClick={() => handleRemoveItem(item.id)} 
                      isLoading={isRemoving === item.id}
                      size="sm"
                      ml={{ base: 0, md: 4 }}
                      fontFamily="'Zain', sans-serif"
                      fontWeight="600"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Remove
                    </MotionButton>
                  </Flex>
                </MotionFlex>
              ))}
            </Stack>
          )
        }
      </Container>

      {/* Sticky Total and Checkout Section - Only shown when cart has items */}
      {cart && cart.items.length > 0 && (
        <Box
          position="sticky"
          bottom="0"
          left="0"
          right="0"
          bg="white"
          borderTop="2px solid"
          borderBottom="2px solid"
          borderColor="gray.400"
          boxShadow="0 -4px 20px rgba(46, 44, 44, 0.1)"
          py={{ base: 4, md: 5 }}
          px={{ base: 4, md: 8 }}
          zIndex={5}
          mt="auto"
        >
          <Container maxW="container.xl">
            <Flex 
              justify="space-between" 
              align="center" 
              direction={{ base: 'column', md: 'row' }}
              gap={{ base: 3, md: 0 }}
            >
              <Text 
                fontSize={{ base: "2xl", md: "2xl", lg: "3xl" }} 
                fontWeight="700"
                fontFamily="'Cormorant', serif"
                color="gray.800"
              >
                Total: Rs<Box as="span" fontFamily="'Zain', sans-serif" color="black"> {cart.total}</Box>
              </Text>
              <MotionButton 
                onClick={handleValidateCart}
                bg="#16a34a"
                color="white"
                size={{ base: "lg", md: "lg" }}
                px={{ base: 8, md: 12 }}
                py={{ base: 6, md: 7 }}
                fontSize={{ base: "lg", md: "xl" }}
                fontFamily="'Zain', sans-serif"
                fontWeight="600"
                isLoading={isCheckingOut}
                _hover={{ bg: "#15803d", transform: "scale(1.02)" }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                w={{ base: "100%", md: "auto" }}
              >
                Proceed to Checkout
              </MotionButton>
            </Flex>
          </Container>
        </Box>
      )}
    </MotionBox>
  );
};

export default CartPage;